import React, { useState, useEffect, useCallback } from 'react';
import { Field, Button } from '@ontruck/orc';
import cx from 'classnames';
import { FormatPrice } from 'utils/format';
import { injectIntl } from 'react-intl';
import { changeItineraryCost } from 'modules/dispatching/service';
import { cellWidth, isLocked } from 'utils/itineraries';

import { DISPATCHING } from 'modules/fleet/events';

import { sanitizeField, isCostAdjustMentValid, getNetMarginValue } from './util';

const t = {
  field_base: 'Base cost',
  field_extra: 'Extra',
  field_margin: 'Margin',
  target_cost: 'Target',
  apply: 'Apply',
  cancel: 'Cancel',
  not_apply: 'N/A',
};

const ItineraryCostCell = ({
  itinerary,
  addLoading,
  removeLoading,
  mutateList,
  showMargin = true,
  showTargetCost = false,
  showTargetCostInRow = true,
  defaultCellWidth = '65px',
  intl,
}) => {
  const isItineraryLocked = isLocked(itinerary);

  const id = itinerary?.id;

  const currencySymbol = intl.messages.currencies[itinerary?.currency];

  const originalCostAdjustment = itinerary?.cost_adjustment;
  const baseNetFullPrice = itinerary?.net_full_price;
  const baseCost = itinerary?.base_cost;

  const [isEditingCost, setIsEditingCost] = useState(false);
  const [costAdjustment, setCostAdjustment] = useState(originalCostAdjustment);
  const [netMargin, setNetMargin] = useState(itinerary?.net_margin);

  const isValidToSubmit = isCostAdjustMentValid(costAdjustment);

  const notCostFound = itinerary && Number(itinerary.net_full_cost) === 0;

  const targetCost = itinerary?.target_cost;
  const isTargetCostFullFilled = itinerary?.net_full_cost <= targetCost;

  const handleEditCost = useCallback(() => {
    if (isItineraryLocked || notCostFound) {
      return;
    }
    setIsEditingCost(true);
  }, [isItineraryLocked, notCostFound]);

  const handleCancelClick = useCallback(() => {
    setIsEditingCost(false);
  }, []);

  const handleCostAdjustment = useCallback((_, { value }) => {
    setCostAdjustment(sanitizeField(value));
  }, []);

  const handleSubmit = useCallback(async () => {
    addLoading && addLoading(id);
    setIsEditingCost(false);
    DISPATCHING.COST_CHANGE({
      itinerary_id: id,
      base_price: baseNetFullPrice,
      base_cost: baseCost,
      cost_adjustment: costAdjustment,
    });
    try {
      await changeItineraryCost(id, { costAdjustment: Number(costAdjustment) });
    } catch (error) {
      console.error(error);
    } finally {
      removeLoading && removeLoading(id);
      mutateList && mutateList();
    }
  }, [costAdjustment, addLoading, removeLoading, mutateList, id, baseNetFullPrice, baseCost]);

  useEffect(() => {
    setNetMargin(
      getNetMarginValue({
        price: baseNetFullPrice,
        cost: baseCost,
        adjustment: costAdjustment,
      })
    );
  }, [baseNetFullPrice, baseCost, costAdjustment]);

  useEffect(() => {
    if (!isEditingCost) {
      setCostAdjustment(originalCostAdjustment);
    }
  }, [isEditingCost, originalCostAdjustment]);

  if (isEditingCost) {
    return (
      <div className="cell cell__cost_adjustment" data-testid="dispatchingListItem__cost_edit">
        <Field
          id="base_cost"
          name="base_cost"
          label={t.field_base}
          value={String(baseCost)}
          className="field"
          asideRightText={currencySymbol}
          disabled
        />
        <Field
          id="extra"
          name="extra"
          label={t.field_extra}
          value={sanitizeField(costAdjustment)}
          className="field"
          asideRightText={currencySymbol}
          onChange={handleCostAdjustment}
        />
        {showMargin && (
          <Field
            id="margin"
            name="margin"
            label={t.field_margin}
            value={netMargin}
            className="field"
            asideRightText={currencySymbol}
            disabled
          />
        )}
        {showTargetCost && (
          <Field
            id="targetCost"
            name="targetCost"
            label={t.target_cost}
            value={targetCost}
            className="field"
            asideRightText={currencySymbol}
            disabled
          />
        )}
        <Button text={t.apply} disabled={!isValidToSubmit} onClick={handleSubmit} />
        <Button text={t.cancel} onClick={handleCancelClick} nude />
      </div>
    );
  }

  const renderCost = () => {
    if (!itinerary) {
      return null;
    }

    if (notCostFound) {
      return <>{t.not_apply}</>;
    }

    return (
      <>
        <span
          style={{ width: 60, textAlign: 'right' }}
          data-testid="dispatchingListItem__base_cost"
        >
          <FormatPrice currency={itinerary?.currency} value={itinerary?.net_full_cost} />
        </span>
        <span
          {...cellWidth('16px')}
          className="cell cell__cost_adjustment cell__nopadding"
          data-testid="dispatchingListItem__cost_adjustment"
        >
          {!!itinerary?.cost_adjustment && (
            <span
              className={cx('value', itinerary.cost_adjustment > 0 ? 'positive' : 'negative')}
            />
          )}
        </span>
        {showTargetCost && showTargetCostInRow && !isTargetCostFullFilled && (
          <span
            className="bad-value"
            data-testid="dispatchingListItem__cost_target"
            style={{ width: 60, textAlign: 'right' }}
          >
            <FormatPrice currency={itinerary?.currency} value={targetCost} />
          </span>
        )}
      </>
    );
  };

  return (
    <>
      <div
        className="cell cell__flex cell__cost cell__right"
        data-testid="dispatchingListItem__cost"
        onDoubleClick={handleEditCost}
      >
        {renderCost()}
      </div>
    </>
  );
};

export default injectIntl(ItineraryCostCell);
